import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { ReactComponent as AzureFocusIcon } from 'shared/img/cloud-providers/azure-focus-cut.svg';
import { ReactComponent as AzureFocusIndicatorIcon } from 'shared/img/cloud-providers/azure-focus-indicator.svg';
import { GenerateIcon, ICONS, Progress } from '@pileus-cloud/anodot-frontend-common';
import { DataTypeProvider, IntegratedSorting, SortingState } from '@devexpress/dx-react-grid';
import { Grid, Table, TableColumnVisibility, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import moment from 'moment';
import { CLOUD_TYPE_IDS, mapUserActionsToAuthorizedUsers, USER_ACTIONS } from 'users/constants/usersConstants';
import LabelCoordinator from 'shared/modules/labelCoordinator';
import TableWrapper from 'shared/components/tables/TableWrapper';
import Checkbox from 'shared/components/andtComponents/Checkbox';
import toast from 'shared/components/andtComponents/Toast';
import Button from 'shared/components/andtComponents/Button';
import DeleteWarningModal from 'shared/components/DeleteWarningModal';
import { getCloudTypeSquareIcon, getVendorMappingsStore } from 'app/store/vendorsStore/vendorStoreProvider';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import { getRoleNamesContainingAccountId } from 'users/utils/rolesUtil';
import { useOnboarding } from 'app/hooks/react-query/useOnboarding';
import getSymbolFromCurrency from 'currency-symbol-map';
import {
  getPageForPausedOnboarding,
  getPageTitleForPausedOnboarding,
  getStepsForOnboarding,
} from 'app/containers/Onboarding/utils/OnboardingUtils';
import { withRolesContextConsumer } from 'users/contexts/RolesProvider';
import { AWS_ONBOARDING_STEPS } from 'app/containers/Onboarding/AwsOnboarding/AwsOnboardingConstants';
import { usePermissions } from 'app/contexts/PermissionsContext';
import { getTableColumns, getTableColumnsWidths, getTableSorting } from '../constants/cloudAccountsConstants';
import styles from './AccountsTable.module.scss';
import { Action, CategoryAction, HierarchicalEntityCategory } from '@anodot-cost/rbac-client';
import AzureFocusMigrationModal from '~/users/containers/CloudAccounts/components/AzureFocusMigrationModal.jsx';
import { useNavigate } from 'react-router';
import EmptyState from 'users/containers/Organization/components/EmptyStates/EmptyState';
import { ReactComponent as CloudAccountsEmptyState } from '../assets/cloud-accounts-empty-state.svg';
import { useAccounts } from 'users/new-user-management/hooks/reactQuery/useAccounts';
import { useRootStore } from 'app/contexts/RootStoreContext';
import CustomHeaderCell from 'shared/components/andtComponents/TableComponents/CustomHeaderCell';
import EditAccountModal from './EditAccountModal';
import EditProcessingCurrencyModal from './EditProcessingCurrencyModal/EditProcessingCurrencyModal';
import ReadOnlyDisplayWrapper from 'shared/components/ReadOnlyDisplayWrapper';
import { useBrand } from 'app/contexts/Brand/BrandContext';
import { ReactComponent as ProcessingCurrencyInfo } from '../assets/processing-currency-info.svg';
import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';
import Spinner from 'shared/components/andtComponents/Spinner';
import { ExternalLinks } from 'shared/enums/external-links.enum';
import { AZURE_ACCOUNT_TYPES } from '~/app/containers/Onboarding/AzureOnboarding/AzureOnboardingConstants.jsx';
import { palette } from 'shared/constants/colorsConstants';

const AccountsTable = ({ data, currencyContext, usersStore, roles, isSearchActive = false }) => {
  const { vendorMappingsStore } = useRootStore();
  const brand = useBrand();
  const vendorStore = getVendorMappingsStore();
  const { invalidateManagementAccounts } = useAccounts();
  const [deleteAccountModalIsOpen, setDeleteAccountModalIsOpen] = useState(null);
  const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(null);
  const [isEditProcessingCurrencyModalOpen, setIsEditProcessingCurrencyModalOpen] = useState(null);
  const [isAzureFocusMigrationModalOpen, setIsAzureFocusMigrationModalOpen] = useState(null);
  const { cancelOnboarding, migrateAzureToFocus } = useOnboarding();
  const { isHasPermission } = usePermissions();
  const { deleteAccount, updateAccountProperties } = useAccounts();
  const handleDeleteAccount = deleteAccount();
  const handleUpdateAccountProperties = updateAccountProperties();
  const handleCancelAccountOnboarding = cancelOnboarding();
  const navigate = useNavigate();

  const dataMap = useMemo(() => {
    const mappedData = data?.reduce((hashMap, item) => {
      const { cloudTypeId } = item;
      hashMap[cloudTypeId] = true;
      return hashMap;
    }, {});
    return mappedData;
  }, [data]);

  const isMSPWithAzureAccounts = useMemo(() => {
    return dataMap[CLOUD_TYPE_IDS.AZURE] && usersStore.isCurrentUserReseller;
  }, [dataMap, usersStore.isCurrentUserReseller]);

  const { tableColumns, tableSorting, tableColumnsWidths } = useMemo(() => {
    const columns = getTableColumns(currencyContext.active, isMSPWithAzureAccounts, brand.name).filter(
      (c) => !c.cloudTypeIds || c.cloudTypeIds.some((ct) => dataMap[ct]) || c.name === 'onboarding',
    );
    return {
      tableColumns: columns,
      tableSorting: getTableSorting(currencyContext.active, isMSPWithAzureAccounts),
      tableColumnsWidths: getTableColumnsWidths(currencyContext.active, isMSPWithAzureAccounts),
    };
  }, [currencyContext.active, brand.name, dataMap, isMSPWithAzureAccounts]);

  const getFilterLabel = (field) => {
    const label = LabelCoordinator.getFieldLabel(field);
    return label;
  };

  const checkIfAccountConnectedToDivisions = (accountId) => {
    const result = usersStore.isAccountConnectedToDivisions(accountId);
    return result;
  };

  const selectAccountForDeleteHandler = (data) => {
    const account = data.row || {};
    if (usersStore.rootStore.appStore.isKeyCloakManagement) {
      setDeleteAccountModalIsOpen(account);
      return;
    }
    if (checkIfAccountConnectedToDivisions(account.accountId)) {
      toast.error(`This Account has connected ${getFilterLabel('Cost Center')} and can't be deleted`);
      return;
    }
    const rolesWithAccount = getRoleNamesContainingAccountId(roles, account.accountId);
    if (rolesWithAccount.length) {
      const rolesText = `${rolesWithAccount.slice(0, 5).join(', ')}
        ${rolesWithAccount.length > 5 ? ` and ${rolesWithAccount.length} more` : ''}`;
      toast.error(`Before deleting account, remove account id from the following roles: ${rolesText}`);
      return;
    }
    setDeleteAccountModalIsOpen(account);
  };

  const updateFilterOutMicrosoft365 = async (accountId, value) => {
    await usersStore.updateFilterOutMicrosoft365AccountProperty(accountId, value);
    invalidateManagementAccounts();
  };

  const deleteAccountHandler = async (action) => {
    if (action === 'delete') {
      if (deleteAccountModalIsOpen.onboarding) {
        await handleCancelAccountOnboarding.mutateAsync({
          accountId: deleteAccountModalIsOpen.accountId,
        });
        return;
      }
      if (usersStore.rootStore.appStore.isKeyCloakManagement) {
        await handleDeleteAccount.mutateAsync({ id: deleteAccountModalIsOpen.accountId });
        await usersStore.usersModel.fetchUser();
        setDeleteAccountModalIsOpen(null);
        return;
      }
      await usersStore.deleteAccount(deleteAccountModalIsOpen);
      invalidateManagementAccounts();
    }
    setDeleteAccountModalIsOpen(null);
  };

  const editAccountHandler = async ({ name, roleARN }) => {
    const { accountId, accountKey } = isEditNameModalOpen;
    try {
      if (usersStore.rootStore.appStore.isKeyCloakManagement) {
        await handleUpdateAccountProperties.mutateAsync({
          id: accountId,
          name,
          roleARN,
        });
        await usersStore.usersModel.fetchUser();
        invalidateManagementAccounts();
      } else {
        await usersStore.updateCurrentDisplayedUserAccount(accountId, accountKey, name, roleARN);
      }
    } catch {
      toast.error('Failed to update account');
    }
    setIsEditNameModalOpen(null);
  };

  const editProcessingCurrencyHandler = async (processingCurrency, setResetInProgress = () => {}, account = {}) => {
    const { accountId, accountKey } = isEditProcessingCurrencyModalOpen || account;
    setResetInProgress(true);
    try {
      await usersStore.updateAccountProcessingCurrency(accountId, accountKey, processingCurrency);
      invalidateManagementAccounts();
    } catch {
      toast.error('Failed to reset processing currency');
    }
    setResetInProgress(false);
    setIsEditProcessingCurrencyModalOpen(null);
  };

  const migrateAzureToFocusHandler = migrateAzureToFocus({
    onSuccess: () => {
      usersStore.usersModel.fetchUser();
      invalidateManagementAccounts();
      toast.success('Account migrated successfully');
    },
  });

  const TableRow = ({ row, children, ...restProps }) => {
    const getRowColor = () => {
      if (row.onboarding) {
        return 'FAFCFF';
      }
      return null;
    };
    return (
      <Table.Row
        {...restProps}
        style={{
          background: getRowColor(),
          hover: {
            background: palette.blue[100],
          },
        }}
      >
        {children || null}
      </Table.Row>
    );
  };

  const detailsFormatter = (data) => {
    const { accountInfo, isFocusMigrationAvailable, onboarding } = data.row;
    if (onboarding) {
      return null;
    }

    const {
      pileus_role_arn: roleARN,
      s3_invoice_bucket: bucket,
      external_id: externalId,
      azure_interface_type: interfaceType,
    } = accountInfo || {};

    return (
      <div className={styles.detailsCell}>
        {bucket && (
          <p>
            <b>Bucket:</b>
            <Tooltip placement="top" title={bucket}>
              {bucket}
            </Tooltip>
          </p>
        )}
        {externalId && (
          <p>
            <b>External ID:</b>{' '}
            <Tooltip placement="top" title={bucket}>
              {externalId}
            </Tooltip>
          </p>
        )}
        {roleARN && (
          <p>
            <b>Role ARN:</b>
            <Tooltip placement="top" title={bucket}>
              {roleARN}
            </Tooltip>
          </p>
        )}
        {interfaceType && (
          <p>
            <b>Interface Type:</b>
            {interfaceType}
          </p>
        )}
        {isFocusMigrationAvailable && (
          <Button
            icon={<AzureFocusIcon className={styles.migrateFocusIcon} />}
            text="Migrate to FOCUS"
            className={{
              button: styles.migrateFocusBtn,
            }}
            onClick={() => {
              setIsAzureFocusMigrationModalOpen(data.row);
            }}
          />
        )}
      </div>
    );
  };

  const EditWithPermmision = ({ Component }) => {
    return (
      <ReadOnlyDisplayWrapper
        isHide={usersStore.isCurrentUserReCustomer}
        key="ReadOnlyDisplayWrapper"
        category={HierarchicalEntityCategory.Accounts}
        action={CategoryAction.Create}
        legacyUserTypeList={mapUserActionsToAuthorizedUsers.get(USER_ACTIONS.ADD_ACCOUNT)}
      >
        {Component}
      </ReadOnlyDisplayWrapper>
    );
  };

  const EditName = ({ data }) => {
    if (data.row.onboarding) {
      return null;
    }
    return (
      <EditWithPermmision
        Component={
          <Button
            text=""
            disabled={
              !isHasPermission(HierarchicalEntityCategory.Accounts, Action.Update) || usersStore.isCurrentUserReCustomer
            }
            isTextButton
            overrideStyles={{ paddingLeft: 0 }}
            onClick={() => {
              setIsEditNameModalOpen(data.row);
            }}
            icon={() => <GenerateIcon iconName={ICONS.edit.name} />}
          />
        }
      ></EditWithPermmision>
    );
  };

  const EditProcessingCurrency = ({ row }) => {
    return (
      <EditWithPermmision
        Component={
          <Button
            text=""
            isTextButton
            overrideStyles={{ paddingLeft: 0, paddingRight: 0 }}
            onClick={() => {
              setIsEditProcessingCurrencyModalOpen(row);
            }}
            icon={() => <GenerateIcon iconName={ICONS.edit.name} />}
          />
        }
      ></EditWithPermmision>
    );
  };

  const LocalResetIcon = ({ resetInProgress, row, setResetInProgress }) => {
    if (resetInProgress) {
      return <Spinner left="8px" top="7px" position="relative" size={15} />;
    }

    if (row.providerCurrency === row.processingCurrency) {
      return null;
    }
    return (
      <Tooltip
        placement="top"
        classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
        title={
          <div className={styles.conversionRateTooltip}>
            <div>Reset to provider currency</div>
          </div>
        }
      >
        <div className={styles.resetIcon}>
          <GenerateIcon
            iconName={ICONS.reset.name}
            onClick={() =>
              editProcessingCurrencyHandler(
                currencyContext.currenciesMap[row.providerCurrency],
                setResetInProgress,
                row,
              )
            }
          />
        </div>
      </Tooltip>
    );
  };

  const DifferentCurrenciesInfo = ({ row }) => {
    if (row.providerCurrency === row.processingCurrency) {
      return null;
    }

    return (
      <div className={styles.differentCurrenciesInfo}>
        <div className={styles.conversionDate}>
          <b>{row.exchangeRate}</b> ({row.conversionDate})
        </div>

        <Tooltip
          placement="top"
          classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
          title={
            <div className={styles.conversionRateTooltip}>
              <div>Conversion rate</div>
              <div>
                <a href={ExternalLinks.CurrenciesDocumentation} target="_blank" className={styles.navigateText}>
                  Learn more
                </a>
              </div>
            </div>
          }
        >
          <ProcessingCurrencyInfo></ProcessingCurrencyInfo>
        </Tooltip>
      </div>
    );
  };
  const ProcessingCurrencyFormatter = ({ row }) => {
    const [resetInProgress, setResetInProgress] = useState(false);

    if (row?.onboarding) {
      return null;
    }
    const Currency = (
      <div>
        {row.processingCurrency} ({getSymbolFromCurrency(row.processingCurrency)})
      </div>
    );
    const isByod = vendorStore.doesCloudTypeIdBelongToByod(row.cloudTypeId);
    if (isByod) {
      return (
        <div className={styles.processingCurrencyContainer}>
          <div className={styles.currencyInfo}>{Currency}</div>
        </div>
      );
    }
    return (
      <div className={styles.processingCurrencyContainer}>
        <div className={styles.currencyInfo}>
          {Currency}
          <EditProcessingCurrency row={row}></EditProcessingCurrency>
          <LocalResetIcon setResetInProgress={setResetInProgress} row={row} resetInProgress={resetInProgress} />
        </div>
        <DifferentCurrenciesInfo row={row}></DifferentCurrenciesInfo>
      </div>
    );
  };

  const accountNameFormatter = (data) => (
    <Tooltip title={data.row.accountName}>
      <div className={styles.accountNameCellContainer}>
        <div className={styles.accountNameCell}>{data.row.accountName}</div>
        <EditName data={data}></EditName>
      </div>
    </Tooltip>
  );

  const accountIdFormatter = (data) => (
    <Tooltip title={data.row.accountId}>
      <div className={styles.accoundIdCell}>{data.row.accountId}</div>
    </Tooltip>
  );

  const cloudTypeFormatter = (data) => {
    const CloudTypeIcon = getCloudTypeSquareIcon(data.row.cloudTypeId);

    return (
      <span className={styles.cloudTypeIcon}>
        <CloudTypeIcon />
        {data.row.accountInfo?.is_focus ? (
          <Tooltip title="FOCUS" classes={{ tooltip: styles.focusTooltip, arrow: styles.focusTooltipArrow }}>
            <AzureFocusIndicatorIcon className={styles.focusIndicator} />
          </Tooltip>
        ) : null}
      </span>
    );
  };

  const lastProcessedFormatter = (data) => {
    const { lastProcessTime, onboarding, ...rest } = data.row;
    const isFailed = rest.validationStatus?.status === 'FAILED' || rest.accountSetupStatus?.status === 'FAILED';
    const getProgress = () => {
      const { cloudTypeId } = rest;
      const steps = getStepsForOnboarding(cloudTypeId);
      const currentStep = getPageForPausedOnboarding(cloudTypeId, rest);
      const pageProgress = ((steps.indexOf(currentStep) + 1) / steps.length) * 100;
      return pageProgress;
    };
    const getTitle = () => {
      const page = getPageForPausedOnboarding(rest.cloudTypeId, rest);
      const { validationStatus, accountSetupStatus, validationShouldStart, userManagementStatus } = rest;
      const isRunning =
        (validationStatus?.status === 'RUNNING' && validationShouldStart) ||
        accountSetupStatus?.status === 'RUNNING' ||
        userManagementStatus?.status === 'RUNNING';
      if (isFailed) {
        return 'Validation failed';
      }
      if (isRunning) {
        return 'Account setup & validation';
      }
      if (page === AWS_ONBOARDING_STEPS.PROCESS_DATA) {
        return 'Invoice processing';
      }
      return `Configuration paused at ${getPageTitleForPausedOnboarding(rest.cloudTypeId, rest)} step`;
    };
    if (onboarding) {
      return (
        <div
          className={classNames(styles.onboardingAccountProgress, {
            [styles.onboardingAccountProgressLong]: currencyContext.active,
          })}
        >
          <Progress value={getProgress()} classes={{ root: styles.progress }} />
          <span data-error={isFailed}>{getTitle()}</span>
          <Button
            onClick={() => {
              navigate(
                `/onboarding/${vendorMappingsStore.getDescriptionById(rest.cloudTypeId)?.toLowerCase()}?accountId=${
                  data.row.accountId
                }`,
              );
            }}
            isTextButton
            overrideStyles={{ height: 20 }}
            text="Open"
          />
        </div>
      );
    }
    return lastProcessTime ? moment(lastProcessTime).format('MMMM DD YYYY') : '';
  };

  const dbCreationTimeFormatter = (data) => {
    const { dbCreationTime, onboarding } = data.row;

    if (onboarding) {
      return null;
    }
    return dbCreationTime ? moment(dbCreationTime).format('MMMM DD YYYY') : '';
  };

  const filterOutMicrosoft365Formatter = (data) => {
    const { filterOutMicrosoft365, accountId, onboarding, cloudTypeId, accountInfo } = data.row;
    const { azure_interface_type: interfaceType } = accountInfo || {};
    if (onboarding || cloudTypeId !== CLOUD_TYPE_IDS.AZURE || interfaceType !== AZURE_ACCOUNT_TYPES.CSP_EXPORT) {
      return null;
    }
    return (
      <Checkbox
        isDisabled={!isHasPermission(HierarchicalEntityCategory.Accounts, Action.Update)}
        primary={filterOutMicrosoft365}
        onChange={() => updateFilterOutMicrosoft365(accountId, !filterOutMicrosoft365)}
        text=""
        isChecked={filterOutMicrosoft365}
      />
    );
  };

  const deleteFormatter = (data) => {
    const isAllAccountsOrMultiCloud = data?.row?.cloudTypeId === CLOUD_TYPE_IDS.MULTI || data?.row?.isAllAccounts;

    return isAllAccountsOrMultiCloud ? null : (
      <Button
        text=""
        disabled={
          !isHasPermission(HierarchicalEntityCategory.Accounts, Action.Update) || usersStore.isCurrentUserReCustomer
        }
        isTextButton
        overrideStyles={{ paddingLeft: 0 }}
        onClick={() => selectAccountForDeleteHandler(data)}
        icon={() => <GenerateIcon iconName={ICONS.delete.name} />}
      />
    );
  };

  return (
    <>
      <div className={styles.container}>
        <Grid rows={data} columns={tableColumns}>
          <SortingState defaultSorting={tableSorting} columnExtensions={tableColumnsWidths} />
          <IntegratedSorting columnExtensions={tableColumnsWidths} />
          <DataTypeProvider for={['accountName']} formatterComponent={accountNameFormatter} />
          <DataTypeProvider for={['accountId']} formatterComponent={accountIdFormatter} />
          <DataTypeProvider for={['cloudTypeId']} formatterComponent={cloudTypeFormatter} />
          <DataTypeProvider for={['details']} formatterComponent={detailsFormatter} />
          <DataTypeProvider for={['delete']} formatterComponent={deleteFormatter} />
          <DataTypeProvider for={['isFilterOutMicrosoft365']} formatterComponent={filterOutMicrosoft365Formatter} />
          <DataTypeProvider for={['lastProcessTime']} formatterComponent={lastProcessedFormatter} />
          <DataTypeProvider for={['dbCreationTime']} formatterComponent={dbCreationTimeFormatter} />
          <DataTypeProvider
            for={['processingCurrency']}
            formatterComponent={(props) => <ProcessingCurrencyFormatter row={props.row}></ProcessingCurrencyFormatter>}
          />
          <TableWrapper
            rowComponent={TableRow}
            columnExtensions={tableColumnsWidths}
            noDataCellComponent={({ colSpan }) => (
              <td colSpan={colSpan} className="border-bottom-0 border-start-0 border-end-0">
                <EmptyState
                  classes={'rolesEmptyState'}
                  mainText={isSearchActive ? 'Could not find the account' : 'Accounts were not onboarded yet'}
                  subTexts={
                    isSearchActive
                      ? ['Refine your search to find the required account']
                      : [
                          'Onboard the relevant Accounts using the onboarding wizard.',
                          'The onboarded account will be visible here and linked accounts derived from it will be listed in the Linked Accounts Tab.',
                        ]
                  }
                  backgroundIcon={<CloudAccountsEmptyState></CloudAccountsEmptyState>}
                ></EmptyState>
              </td>
            )}
          />
          <TableColumnVisibility hiddenColumnNames={['onboarding']} onHiddenColumnNamesChange={() => null} />
          <TableHeaderRow showSortingControls={true} cellComponent={CustomHeaderCell} />
        </Grid>
      </div>
      <DeleteWarningModal
        modalTitle={deleteAccountModalIsOpen?.onboarding ? 'Delete Onboarding' : 'Delete Account'}
        handleDelete={deleteAccountHandler}
        isOpen={!!deleteAccountModalIsOpen}
        deletedItemName={deleteAccountModalIsOpen?.accountName}
      />
      {isEditNameModalOpen && (
        <EditAccountModal
          accountData={isEditNameModalOpen}
          onClose={() => setIsEditNameModalOpen(null)}
          isOpen
          editAccountHandler={editAccountHandler}
        />
      )}
      {isEditProcessingCurrencyModalOpen && (
        <EditProcessingCurrencyModal
          account={isEditProcessingCurrencyModalOpen}
          onClose={() => setIsEditProcessingCurrencyModalOpen(null)}
          isOpen
          editProcessingCurrencyHandler={editProcessingCurrencyHandler}
          usersStore={usersStore}
          currencyContext={currencyContext}
        />
      )}
      {isAzureFocusMigrationModalOpen && (
        <AzureFocusMigrationModal
          accountData={isAzureFocusMigrationModalOpen}
          onClose={() => setIsAzureFocusMigrationModalOpen(null)}
          isOpen
          onSave={migrateAzureToFocusHandler.mutateAsync}
        />
      )}
    </>
  );
};


const ObserverAccountsTable = withRolesContextConsumer(observer(AccountsTable));

export default ObserverAccountsTable;
